// Revise runs: read-side helpers for resume and the before/after comparison.
//
// Everything a run records goes through the ordinary onActivityResponse →
// learning_interactions path (one row per assessed item, keyed by question id,
// plus one `__results` row on completion). These two reads are the other half:
//
//   fetchReviseProgress   — the learner's latest row per activity key on ONE
//                           slide, so a reload or a return visit resumes where
//                           they were instead of starting over. This replaces
//                           the in-memory `state` object the old files lost on
//                           reload, and the shared localStorage key that let
//                           two Practice Labs clobber each other.
//   fetchReviseSibling    — the stored `__results` row of a check's OTHER
//                           sitting, matched by pairKey. Replaces `?prior=`,
//                           which read a score off the iframe URL that nothing
//                           ever populated.
//
// Both resolve the enrollment themselves, return null/empty on any failure and
// never throw: a run must render with or without history. RLS scopes the read
// to the caller's own rows; there is no admin override here.

import { supabase } from '../lib/supabase';
import { isLearningInteractionsEnabled, writeInteraction, type WriteInteractionResult } from './learningInteractionService';
import { REVISE_RESULTS_KEY } from '../lib/revise/types';
import type { Slide } from '../components/course-builder/slides/slideTypes';

async function ownEnrollmentId(courseId: string): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('enrollments')
    .select('id')
    .eq('user_id', user.id)
    .eq('course_id', courseId)
    .maybeSingle();
  return data?.id ?? null;
}

/** Latest response per activity key for this learner on this slide. */
export async function fetchReviseProgress(
  courseId: string,
  slideId: string,
): Promise<Record<string, Record<string, unknown>>> {
  try {
    if (!(await isLearningInteractionsEnabled())) return {};
    const enrollmentId = await ownEnrollmentId(courseId);
    if (!enrollmentId) return {};
    // Newest first, paged: a long paper plus repeated attempts can exceed one
    // page, and the oldest rows can hold the only response for an early
    // question. Keep the first row seen per key; stop when a page comes back
    // short (the last page) or after a sane number of pages.
    const PAGE = 400;
    const MAX_PAGES = 10;
    const out: Record<string, Record<string, unknown>> = {};
    for (let page = 0; page < MAX_PAGES; page++) {
      const { data, error } = await supabase
        .from('learning_interactions')
        .select('activity_key, response, server_ts')
        .eq('slide_id', slideId)
        .eq('enrollment_id', enrollmentId)
        .order('server_ts', { ascending: false })
        .range(page * PAGE, page * PAGE + PAGE - 1);
      if (error || !data) break;
      for (const row of data as Array<{ activity_key: string; response: Record<string, unknown> | null }>) {
        if (row.activity_key in out) continue;
        if (row.response) out[row.activity_key] = row.response;
      }
      if (data.length < PAGE) break;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * The other sitting's stored results for a knowledge check, or null. The
 * `__results` payload carries `pairKey` and `sitting` precisely so this can be
 * a containment filter rather than a slide-id lookup — the two sittings are
 * different slides, usually in different lessons.
 */
export async function fetchReviseSibling(
  courseId: string,
  pairKey: string,
  sitting: 'before' | 'after',
): Promise<Record<string, unknown> | null> {
  try {
    if (!(await isLearningInteractionsEnabled())) return null;
    const enrollmentId = await ownEnrollmentId(courseId);
    if (!enrollmentId) return null;
    const { data, error } = await supabase
      .from('learning_interactions')
      .select('response, server_ts')
      .eq('enrollment_id', enrollmentId)
      .eq('activity_key', REVISE_RESULTS_KEY)
      .contains('response', { pairKey, sitting })
      .order('server_ts', { ascending: false })
      .limit(1);
    if (error || !data || data.length === 0) return null;
    return (data[0] as { response: Record<string, unknown> | null }).response ?? null;
  } catch {
    return null;
  }
}

/**
 * Save a workshop draft (a paragraph plan, a written part) as ONE
 * learning_interactions row per (slide, key), upserted on a stable client id
 * the way an embed checkpoint is (activityService.recordEmbedCheckpoint).
 * Writing changes every few seconds and runs to a thousand words, so an
 * append per save would fill the table reporting reads from
 * (KULA-REVISE-WORKSHOP-PLAN.md §5.2, closed by §0b without a table). Resume
 * reads it back through fetchReviseProgress like every other row. Best
 * effort: not enrolled (staff previewing) is a skip, not an error.
 */
export async function saveReviseDraft(courseId: string, lessonId: string, slide: Slide, key: string, payload: Record<string, unknown>): Promise<WriteInteractionResult> {
  try {
    if (!(await isLearningInteractionsEnabled())) return { written: false, skipReason: 'flag-off' };
    const enrollmentId = await ownEnrollmentId(courseId);
    if (!enrollmentId) return { written: false, skipReason: 'not-enrolled' };
    return await writeInteraction({
      enrollmentId,
      lessonId,
      slide,
      activityKey: key,
      verb: 'responded',
      response: { questionId: key, ...payload },
      clientId: `${slide.id}:${key}`,
    });
  } catch (e) {
    return { written: false, skipReason: `error:${(e as Error).message}` };
  }
}
